import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SalesSummary } from '../components/SalesSummary';
import { EditAddressDialog } from '../components/EditAddressDialog';
import { ArrowLeft, Printer, Package, Truck, CheckCircle, XCircle, Clock, FileText, CreditCard, ExternalLink, Pencil } from 'lucide-react';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersAdminPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [filterStatus]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const url = filterStatus ? `${API}/orders?status=${filterStatus}` : `${API}/orders`;
      const response = await axios.get(url);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoadError(error.response?.data?.detail || error.message || 'Error desconocido');
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los pedidos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, trackingNumber = null) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (trackingNumber) {
        params.append('tracking_number', trackingNumber);
      }

      await axios.patch(`${API}/orders/${orderId}/status?${params}`);
      
      toast({
        title: 'Estado actualizado',
        description: `Pedido marcado como ${newStatus}`
      });
      
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive'
      });
    }
  };

  // Descarga el blob como PDF Y abre el diálogo de impresión del navegador
  // Abre la pestaña al pulsar (antes de esperar al servidor) para que el navegador no la bloquee
  const openPdfTab = () => {
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:24px">Generando PDF…</p>');
    return win;
  };

  // Muestra el PDF en una pestaña nueva (funciona en Safari, Chrome y móvil) e intenta abrir el diálogo de impresión
  const downloadAndPrint = (blob, filename, win) => {
    const url = window.URL.createObjectURL(blob);
    if (win && !win.closed) {
      win.document.open();
      win.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><title>${filename}</title>
<style>body{margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f2f2f2;display:flex;flex-direction:column;height:100vh}
.bar{display:flex;gap:10px;padding:12px;background:#000;align-items:center}
.bar button,.bar a{font:700 14px sans-serif;letter-spacing:1px;padding:12px 22px;border:0;border-radius:999px;cursor:pointer;text-decoration:none}
#p{background:#fff;color:#000}#d{background:#333;color:#fff}
iframe{flex:1;border:0;width:100%;background:#fff}</style></head>
<body><div class="bar"><button id="p" data-testid="pdf-print-btn">IMPRIMIR</button><a id="d" href="${url}" download="${filename}" data-testid="pdf-download-btn">DESCARGAR PDF</a></div>
<iframe id="f" src="${url}"></iframe>
<script>
var f=document.getElementById('f');
function pr(){try{f.contentWindow.focus();f.contentWindow.print();}catch(e){window.print();}}
document.getElementById('p').onclick=pr;
f.addEventListener('load',function(){setTimeout(pr,400);});
</script></body></html>`);
      win.document.close();
      toast({ title: 'PDF listo', description: 'Pulsa IMPRIMIR en la pestaña nueva.' });
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: 'PDF descargado', description: `Abre ${filename} desde Descargas para imprimirlo.` });
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 600000);
  };

  const printShippingLabel = async (order) => {
    const win = openPdfTab();
    try {
      const response = await axios.post(`${API}/orders/${order.id}/print-label`, {}, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadAndPrint(blob, `etiqueta-${order.order_number}.pdf`, win);
      toast({
        title: 'Etiqueta lista',
        description: 'Se ha abierto el diálogo de impresión y la etiqueta se ha guardado'
      });
    } catch (error) {
      win?.close();
      console.error('Error printing label:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar la etiqueta',
        variant: 'destructive'
      });
    }
  };

  const openStripePayment = async (order) => {
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:24px">Abriendo Stripe…</p>');
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}/stripe-link`);
      if (win && !win.closed) win.location.href = data.url;
      else window.location.href = data.url;
      if (!data.exact) toast({ title: 'Búsqueda en Stripe', description: 'No encontré el pago exacto; te abro la búsqueda en Stripe.' });
    } catch (error) {
      win?.close();
      toast({ title: 'Error', description: 'No se pudo abrir el pago en Stripe', variant: 'destructive' });
    }
  };

  const printInvoice = async (order) => {
    const win = openPdfTab();
    try {
      const response = await axios.post(`${API}/orders/${order.id}/print-invoice`, {}, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadAndPrint(blob, `factura-${order.order_number}.pdf`, win);
      toast({
        title: 'Factura lista',
        description: 'Se ha abierto el diálogo de impresión y la factura se ha guardado'
      });
    } catch (error) {
      win?.close();
      console.error('Error printing invoice:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar la factura',
        variant: 'destructive'
      });
    }
  };

  // Albarán rápido imprimible desde el navegador (sin backend) — ideal para meter dentro de la caja
  const printPackingSlip = (order) => {
    const addr = order.shipping_address || {};
    const items = order.items || [];
    const created = order.created_at
      ? new Date(order.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';
    const itemsRows = items.map((it) => `
      <tr>
        <td style="padding:8px 10px;border-bottom:1px solid #eee">${it.name || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${it.size || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${it.color || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${it.quantity || 1}</td>
      </tr>
    `).join('');

    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Albarán ${order.order_number}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; margin: 0; }
  .wrap { max-width: 720px; margin: 0 auto; padding: 24px; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 16px; }
  .brand { font-weight: 900; font-size: 28px; letter-spacing: -0.5px; }
  .brand small { display:block; font-size: 11px; letter-spacing: 3px; font-weight: 700; color:#666; }
  .meta { text-align: right; font-size: 12px; color:#555; }
  .meta strong { display:block; color:#000; font-size:16px; }
  h2 { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color:#666; margin:24px 0 8px; }
  table { width:100%; border-collapse: collapse; margin-top: 6px; font-size: 13px; }
  th { background:#000; color:#fff; text-align:left; padding:10px; font-size: 11px; letter-spacing: 2px; }
  th.center { text-align:center; }
  .box { border:1px solid #e5e5e5; padding:14px; font-size: 13px; line-height:1.6; }
  .foot { margin-top: 32px; padding-top: 14px; border-top: 1px dashed #ccc; font-size:11px; color:#666; text-align:center; }
  .foot strong { color:#000; }
  @media print { .noprint { display:none !important; } }
</style>
</head>
<body>
  <div class="wrap">
    <div class="top">
      <div class="brand">JRF SPORTSWEAR<small>ALBARÁN</small></div>
      <div class="meta">
        <strong>${order.order_number || order.id}</strong>
        Fecha: ${created}<br/>
        Estado: ${order.status || '-'}
      </div>
    </div>

    <h2>Envío a</h2>
    <div class="box">
      <strong>${addr.full_name || '—'}</strong><br/>
      ${addr.address || ''}${addr.address ? '<br/>' : ''}
      ${[addr.postal_code, addr.city].filter(Boolean).join(' ')}${addr.city ? '<br/>' : ''}
      ${addr.province ? addr.province + '<br/>' : ''}
      ${addr.country || ''}<br/>
      ${addr.phone ? 'Tel: ' + addr.phone + '<br/>' : ''}
      ${addr.email ? '<small style="color:#777">' + addr.email + '</small>' : ''}
    </div>

    <h2>Productos</h2>
    <table>
      <thead>
        <tr>
          <th>Prenda</th>
          <th class="center">Talla</th>
          <th class="center">Color</th>
          <th class="center">Uds</th>
        </tr>
      </thead>
      <tbody>${itemsRows || '<tr><td colspan="4" style="padding:14px;text-align:center;color:#999">Sin artículos</td></tr>'}</tbody>
    </table>

    ${order.notes ? `<h2>Notas de la clienta</h2><div class="box">${String(order.notes).replace(/</g,'&lt;')}</div>` : ''}

    <div class="foot">
      ¡Gracias por confiar en <strong>JRF SPORTSWEAR</strong>!<br/>
      Para dudas: infojrfsportswear@gmail.com · WhatsApp +34 611 871 722
    </div>
  </div>
  <script>
    window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 200); });
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      toast({
        title: 'Permite ventanas emergentes',
        description: 'Tu navegador bloqueó la ventana de impresión. Activa los pop-ups para este sitio.',
        variant: 'destructive'
      });
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // === BULK: todas las etiquetas en un solo PDF ===
  const printAllLabels = async (ordersList) => {
    const ids = (ordersList || []).map((o) => o.id);
    if (ids.length === 0) return;
    const win = openPdfTab();
    try {
      const response = await axios.post(
        `${API}/orders/print-labels-bulk`,
        { order_ids: ids },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadAndPrint(blob, `etiquetas-${new Date().toISOString().slice(0, 10)}.pdf`, win);
      toast({
        title: `${ids.length} etiquetas listas`,
        description: 'Se ha abierto el diálogo de impresión con todas las etiquetas'
      });
    } catch (error) {
      win?.close();
      console.error('Error bulk labels:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron generar las etiquetas',
        variant: 'destructive'
      });
    }
  };

  // === BULK: todos los albaranes en una sola hoja imprimible ===
  const printAllPackingSlips = (ordersList) => {
    const list = ordersList || [];
    if (list.length === 0) return;

    const slip = (order) => {
      const addr = order.shipping_address || {};
      const items = order.items || [];
      const created = order.created_at
        ? new Date(order.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '—';
      const rows = items.map((it) => `
        <tr>
          <td style="padding:8px 10px;border-bottom:1px solid #eee">${(it.name || '').replace(/</g,'&lt;')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${it.size || '-'}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${(it.color || '-').replace(/</g,'&lt;')}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center">${it.quantity || 1}</td>
        </tr>
      `).join('');

      return `
        <section class="slip">
          <div class="top">
            <div class="brand">JRF SPORTSWEAR<small>ALBARÁN</small></div>
            <div class="meta">
              <strong>${order.order_number || order.id}</strong>
              Fecha: ${created}<br/>
              Estado: ${order.status || '-'}
            </div>
          </div>

          <h2>Envío a</h2>
          <div class="box">
            <strong>${(addr.full_name || '—').replace(/</g,'&lt;')}</strong><br/>
            ${(addr.address || '').replace(/</g,'&lt;')}${addr.address ? '<br/>' : ''}
            ${[addr.postal_code, addr.city].filter(Boolean).join(' ')}${addr.city ? '<br/>' : ''}
            ${addr.province ? addr.province + '<br/>' : ''}
            ${addr.country || ''}<br/>
            ${addr.phone ? 'Tel: ' + addr.phone + '<br/>' : ''}
            ${addr.email ? '<small style="color:#777">' + addr.email + '</small>' : ''}
          </div>

          <h2>Productos</h2>
          <table>
            <thead>
              <tr>
                <th>Prenda</th>
                <th class="center">Talla</th>
                <th class="center">Color</th>
                <th class="center">Uds</th>
              </tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="4" style="padding:14px;text-align:center;color:#999">Sin artículos</td></tr>'}</tbody>
          </table>

          ${order.notes ? `<h2>Notas de la clienta</h2><div class="box">${String(order.notes).replace(/</g,'&lt;')}</div>` : ''}

          <div class="foot">
            ¡Gracias por confiar en <strong>JRF SPORTSWEAR</strong>!<br/>
            Para dudas: infojrfsportswear@gmail.com · WhatsApp +34 611 871 722
          </div>
        </section>
      `;
    };

    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Albaranes · ${list.length} pedidos</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; margin: 0; }
  section.slip { max-width: 720px; margin: 0 auto; padding: 24px; page-break-after: always; }
  section.slip:last-child { page-break-after: auto; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 16px; }
  .brand { font-weight: 900; font-size: 28px; letter-spacing: -0.5px; }
  .brand small { display:block; font-size: 11px; letter-spacing: 3px; font-weight: 700; color:#666; }
  .meta { text-align: right; font-size: 12px; color:#555; }
  .meta strong { display:block; color:#000; font-size:16px; }
  h2 { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color:#666; margin:24px 0 8px; }
  table { width:100%; border-collapse: collapse; margin-top: 6px; font-size: 13px; }
  th { background:#000; color:#fff; text-align:left; padding:10px; font-size: 11px; letter-spacing: 2px; }
  th.center { text-align:center; }
  .box { border:1px solid #e5e5e5; padding:14px; font-size: 13px; line-height:1.6; }
  .foot { margin-top: 32px; padding-top: 14px; border-top: 1px dashed #ccc; font-size:11px; color:#666; text-align:center; }
  .foot strong { color:#000; }
</style>
</head>
<body>
  ${list.map(slip).join('')}
  <script>
    window.addEventListener('load', () => { setTimeout(() => { window.focus(); window.print(); }, 300); });
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      toast({
        title: 'Permite ventanas emergentes',
        description: 'Tu navegador bloqueó la ventana de impresión. Activa los pop-ups para este sitio.',
        variant: 'destructive'
      });
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    toast({
      title: `${list.length} albaranes listos`,
      description: 'Se ha abierto el diálogo de impresión con todos los albaranes'
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pendiente': return <Clock className="w-4 h-4" />;
      case 'pagado': case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'procesando': return <Package className="w-4 h-4" />;
      case 'enviado': return <Truck className="w-4 h-4" />;
      case 'entregado': return <CheckCircle className="w-4 h-4" />;
      case 'cancelado': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'pagado': case 'paid': return 'bg-sky-100 text-sky-700 border-sky-300';
      case 'procesando': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'enviado': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'entregado': return 'bg-green-100 text-green-700 border-green-300';
      case 'cancelado': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex flex-col">
      <div className="flex-1">
      
      <div className="container mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al catálogo
        </Button>

        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h1 className="text-3xl font-bold">Gestión de Pedidos</h1>
          
          <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos los pedidos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="pagado">Pagado</SelectItem>
              <SelectItem value="procesando">Procesando</SelectItem>
              <SelectItem value="enviado">Enviado</SelectItem>
              <SelectItem value="entregado">Entregado</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <SalesSummary refreshKey={orders.length} />

        {editingOrder && (
          <EditAddressDialog
            key={editingOrder.id}
            order={editingOrder}
            open={!!editingOrder}
            onOpenChange={(o) => !o && setEditingOrder(null)}
            onSaved={fetchOrders}
            toast={toast}
          />
        )}

        {/* Acciones en bloque sobre todos los pedidos visibles */}
        {!loading && !loadError && orders.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="bulk-actions">
            <span className="text-xs text-neutral-500 uppercase tracking-widest self-center mr-1">
              {orders.length} pedido{orders.length === 1 ? '' : 's'} visible{orders.length === 1 ? '' : 's'} ·
            </span>
            <Button
              onClick={() => printAllLabels(orders)}
              variant="outline"
              className="border-black"
              data-testid="print-all-labels"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir todas las etiquetas
            </Button>
            <Button
              onClick={() => printAllPackingSlips(orders)}
              variant="outline"
              className="border-black"
              data-testid="print-all-packing"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir todos los albaranes
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400"></div>
          </div>
        ) : loadError ? (
          <Card className="p-16 text-center border-red-200 bg-red-50" data-testid="orders-load-error">
            <Package className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h3 className="text-xl font-semibold text-red-700 mb-2">Error cargando pedidos</h3>
            <p className="text-sm text-red-600 mb-4 break-words">{loadError}</p>
            <button
              onClick={fetchOrders}
              className="bg-black text-white text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 hover:bg-neutral-800"
              data-testid="retry-orders"
            >
              Reintentar
            </button>
          </Card>
        ) : orders.length === 0 ? (
          <Card className="p-16 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay pedidos</h3>
            <p className="text-gray-500">Los pedidos aparecerán aquí cuando los clientes realicen compras</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const addr = order.shipping_address || {};
              return (
              <Card key={order.id} className="p-6" data-testid={`order-${order.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* Order info */}
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold">{order.order_number}</h3>
                      <Badge className={getStatusColor(order.status)}>
                        {getStatusIcon(order.status)}
                        <span className="ml-1">{order.status}</span>
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Cliente:</strong> {addr.full_name || addr.name || order.customer_info?.name || '—'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Email:</strong> {addr.email || '—'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Teléfono:</strong> {addr.phone || '—'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Dirección:</strong> {[addr.address, addr.postal_code, addr.city, addr.province].filter(Boolean).join(', ') || '—'}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Método de pago:</strong> {order.payment_method || '—'}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setEditingOrder(order)}
                      data-testid={`edit-address-${order.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-2" />
                      Editar dirección / teléfono
                    </Button>
                  </div>

                  {/* Order items */}
                  <div>
                    <h4 className="font-semibold mb-2">Productos ({(order.items || []).length})</h4>
                    <div className="space-y-1">
                      {(order.items || []).map((item, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {item.name} ({item.size}) x{item.quantity}
                        </p>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <p className="font-bold text-lg text-pink-600">Total: {(order.total ?? 0).toFixed(2)}€</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <Label className="text-xs">Cambiar estado</Label>
                    <Select
                      value={order.status === 'paid' ? 'pagado' : order.status}
                      onValueChange={(value) => updateOrderStatus(order.id, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="pagado">Pagado</SelectItem>
                        <SelectItem value="procesando">Procesando</SelectItem>
                        <SelectItem value="enviado">Enviado</SelectItem>
                        <SelectItem value="entregado">Entregado</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>

                    {order.tracking_number && (
                      <div className="mt-2">
                        <Label className="text-xs">Número de seguimiento</Label>
                        <p className="text-sm font-mono bg-gray-50 p-2 rounded">{order.tracking_number}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button
                        onClick={() => printShippingLabel(order)}
                        variant="outline"
                        className="flex-1 min-w-[90px]"
                        data-testid={`print-label-${order.id}`}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Etiqueta
                      </Button>
                      <Button
                        onClick={() => printInvoice(order)}
                        variant="outline"
                        className="flex-1 min-w-[90px]"
                        data-testid={`print-invoice-${order.id}`}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Factura
                      </Button>
                      <Button
                        onClick={() => printPackingSlip(order)}
                        variant="outline"
                        className="flex-1 min-w-[90px]"
                        data-testid={`print-packing-${order.id}`}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Albarán
                      </Button>
                      {order.payment_method === 'stripe' && (
                        <Button
                          onClick={() => openStripePayment(order)}
                          className="w-full bg-[#635bff] hover:bg-[#5146e5] text-white"
                          data-testid={`stripe-link-${order.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Ver pago en Stripe
                          <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>
        )}
      </div>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default OrdersAdminPage;
